import React, { useMemo } from "react";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Grid from "components/common/Grid";
import { usePhcTheme } from "../PhcThemeContext";
import { buildRatioGlossary } from "../ratioGlossaryData";

const DISCLAIMER_PARAGRAPHS = [
  "The analysis of the securities [Report] is for information and educational purposes only. It should not be construed as legal, tax, investment, financial, or other advice. It does not constitute a solicitation, recommendation, endorsement, or offer to buy or sell any securities. You can use the Report for your personal, non-commercial use only.",
  "The Report provides general technical information and does not address the circumstances of any particular individual or entity.",
  "You alone assume the sole responsibility of evaluating the merits and risks associated with the use of the Report before making any decisions based on the Report. Jarvis accepts no liability for any damages or losses however caused, in connection with investments made depending on the Report.",
  "You agree not to hold Jarvis liable for any possible claim for damages arising from any decision you make based on the Report.",
  "The Report is provided “as is” and without warranties of any kind. You bear all risks associated with the use of the Report, including without limitation, any reliance on the accuracy, completeness, or usefulness of the Report. Jarvis and its employees, officers, directors, agents, and representatives disclaim all warranties, express or implied, including, without limitation, all warranties of title, non-infringement, accuracy, completeness, usefulness, merchantability, and fitness for a particular use, and warranties that may arise depending on the report.",
  "Your exclusive remedy for dissatisfaction with the Report is to stop using the Report. Jarvis is not liable for any direct, indirect, incidental, consequential, special, or punitive damages, under any theory of liability, including without limitation, damages for loss of profits, use, data, or loss of other intangibles. In particular, and without limitation, Jarvis will not be liable for damages of any kind resulting from using the report.",
  "Investment in securities is subject to market risks, please carry out your due diligence before investing. Past performance is not indicative of future returns.",
  "Registration granted by SEBI, membership of BASL, and certification from NISM in no way guarantee the performance of the IA or provide any assurance of returns to investors.",
];

const getInsightLabel = (key) => {
  if (key === "sortino") return "INSIGHT";
  if (key === "beta") return "MARKET SENTIMENT";
  if (key === "volatility") return "RISK METRIC";
  if (key === "liquidity") return "PORTFOLIO STATUS";
  return "INTERPRETATION";
};

const GlossaryCard = ({ card }) => {
  const { theme } = usePhcTheme();

  return (
  <Box
    sx={{
      position: "relative",
      width: "100%",
      height: "100%",
      borderRadius: { xs: "16px", md: "20px" },
      border: {
        xs: theme.mobileMetaBorder,
        md: `1px solid ${theme.sectionBorder}`,
      },
      backgroundImage: { xs: "none", md: theme.sectionBg },
      backgroundColor: { xs: theme.mobileCardBg, md: theme.cardBg },
      boxShadow: { xs: "none", md: theme.cardShadow },
      overflow: "hidden",
      p: { xs: "21px", md: "24px" },
      "&::after": {
        content: { xs: "none", md: '""' },
        position: "absolute",
        inset: 0,
        borderRadius: "inherit",
        pointerEvents: "none",
        boxShadow: theme.insetGlow,
      },
    }}
  >
    <Stack spacing="8px" sx={{ position: "relative", zIndex: 1 }}>
      <Text
        sx={{
          fontWeight: 600,
          fontSize: { xs: 20, md: 18 },
          color: theme.textPrimary,
          lineHeight: { xs: "28px", md: "normal" },
        }}
      >
        {card.title}
      </Text>
      <Text
        sx={{
          fontWeight: card.lightBody ? 300 : 400,
          fontSize: 14,
          color: { xs: theme.textMuted, md: card.lightBody ? theme.textPrimary : theme.textMuted },
          lineHeight: { xs: "23px", md: card.lightBody ? 1.7 : "normal" },
          letterSpacing: { xs: "normal", md: card.lightBody ? "-0.42px" : "normal" },
        }}
      >
        {card.description}
      </Text>
      {card.interpretation && (
        <Box
          sx={{
            mt: { xs: 1, md: 0 },
            borderRadius: { xs: "14px", md: 0 },
            border: { xs: theme.mobileMetaBorder, md: "none" },
            bgcolor: { xs: theme.mobileMetaBg, md: "transparent" },
            px: { xs: "13px", md: 0 },
            py: { xs: "17px", md: 0 },
          }}
        >
          <Text
            sx={{
              fontWeight: 700,
              fontSize: 12,
              color: theme.textSecondary,
              lineHeight: "16px",
              letterSpacing: "0.02em",
              mb: { xs: 1, md: 0.75 },
            }}
          >
            {getInsightLabel(card.key)}
          </Text>
          <Text
            sx={{
              fontWeight: 400,
              fontSize: 14,
              color: theme.textMuted,
              lineHeight: { xs: "16px", md: "normal" },
            }}
          >
            {String(card.interpretation).replace(/^Interpretation:\s*/i, "")}
          </Text>
        </Box>
      )}
    </Stack>
  </Box>
  );
};

const RatioGlossary = ({
  metrics = [],
  beta,
  liquiditySynopsis,
  stockCount,
}) => {
  const { theme } = usePhcTheme();
  const cards = useMemo(
    () =>
      buildRatioGlossary({
        metrics,
        beta,
        liquiditySynopsis,
        stockCount,
      }),
    [metrics, beta, liquiditySynopsis, stockCount],
  );

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: { xs: 0, md: "20px" },
        border: { xs: "none", md: `1px solid ${theme.sectionBorder}` },
        backgroundColor: { xs: "transparent", md: theme.cardBg },
        boxShadow: {
          xs: "none",
          md: "0px -6px 6px -1px rgba(0,0,0,0.1), 0px -8px 4px -2px rgba(0,0,0,0.1)",
        },
        overflow: "hidden",
        px: { xs: 0, md: "19px" },
        py: { xs: 0, md: "42px" },
        "&::after": {
          content: { xs: "none", md: '""' },
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: theme.sectionInsetGlow,
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1, mb: { xs: 3, md: "46px" } }}>
        <Text
          sx={{
            fontWeight: 700,
            fontSize: { xs: 24, md: 24 },
            color: theme.textPrimary,
            letterSpacing: { xs: "normal", md: "-0.72px" },
            lineHeight: { xs: "32px", md: "none" },
            mb: { xs: 0.5, md: 0 },
          }}
        >
          Ratio Glossary
        </Text>
        <Text
          sx={{
            display: { xs: "block", md: "none" },
            fontWeight: 400,
            fontSize: 14,
            color: theme.mobileLabelColor,
            lineHeight: "20px",
            maxWidth: 310,
          }}
        >
          Key performance metrics to evaluate your portfolio performance and risk
          profile.
        </Text>
      </Box>

      <Grid
        container
        spacing={{ xs: 3, md: "24px" }}
        sx={{ position: "relative", zIndex: 1 }}
      >
        {cards.map((card) => (
          <Grid
            item
            xs={12}
            sm={12}
            md={card.span === 6 ? 6 : 4}
            key={card.key}
            sx={{ display: "flex" }}
          >
            <GlossaryCard card={card} />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ position: "relative", zIndex: 1, mt: { xs: 4, md: "46px" } }}>
        <Text
          sx={{
            fontWeight: 700,
            fontSize: { xs: 14, md: 16 },
            color: theme.textPrimary,
            mb: 1.5,
          }}
        >
          Disclaimer:
        </Text>
        <Stack spacing={1.5}>
          {DISCLAIMER_PARAGRAPHS.map((paragraph) => (
            <Text
              key={paragraph}
              sx={{
                fontWeight: 400,
                fontSize: { xs: 12, md: 13 },
                color: theme.textMuted,
                lineHeight: 1.6,
              }}
            >
              {paragraph}
            </Text>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default RatioGlossary;
